/**
 * The rows of the attachment popover's four pickers, each from the read the mobile app makes for
 * it: the exercise library (the catalogue's own cache entry, in the content language), the client's
 * plan assignments (`GET /plan-assignment/customer/{id}`), and their recent finished sessions
 * (`GET /customer/{id}/recent-sessions`, fifteen of them — the phone's count).
 *
 * A row carries the `CardChoice` its press sends, with the snapshot the bubble draws until the
 * server's own arrives.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  exerciseLibraryQueryOptions,
  muscleGroupLabelKey,
  stillUrl,
  toExerciseListItems,
} from '~/entities/exercise-library';
import { getCustomerIdRecentSessions } from '~/shared/api/generated/endpoints/customer/customer';
import { getPlanAssignmentCustomerCustomerId } from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import type { CardChoice } from '../model/chatSession';

export type PickerKind = 'exercise' | 'plan' | 'summary' | 'clipRequest';

export interface PickerRow {
  readonly key: string;
  readonly title: string;
  readonly meta: string;
  readonly image: string | null;
  readonly choice: CardChoice;
}

export interface PickerRows {
  readonly rows: readonly PickerRow[];
  readonly pending: boolean;
  readonly failed: boolean;
}

export function usePickerRows(kind: PickerKind, customerId: number | null): PickerRows {
  const { t, i18n } = useTranslation();
  const lang = useContentLanguage();

  const exercises = useQuery({
    ...exerciseLibraryQueryOptions(lang),
    enabled: kind === 'exercise' || kind === 'clipRequest',
    select: toExerciseListItems,
  });
  const plans = useQuery({
    queryKey: ['chat', 'picker', 'plans', customerId],
    queryFn: ({ signal }) => getPlanAssignmentCustomerCustomerId(customerId ?? 0, undefined, signal),
    enabled: kind === 'plan' && customerId !== null,
  });
  const sessions = useQuery({
    queryKey: ['chat', 'picker', 'sessions', customerId, lang],
    queryFn: ({ signal }) =>
      getCustomerIdRecentSessions(customerId ?? 0, { count: 15, lang }, undefined, signal),
    enabled: kind === 'summary' && customerId !== null,
  });

  const dateLabel = (iso: string | null | undefined) =>
    typeof iso === 'string' && iso !== ''
      ? new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric', month: 'long' }).format(
          new Date(iso),
        )
      : '';

  if (kind === 'exercise' || kind === 'clipRequest') {
    return {
      pending: exercises.isPending,
      failed: exercises.isError,
      rows: (exercises.data ?? []).map((item) => {
        const muscle = item.primaryMuscles[0];
        const muscleName = muscle === undefined ? '' : t(muscleGroupLabelKey(muscle));
        const image = stillUrl(item);
        return {
          key: String(item.id),
          title: item.name,
          meta: muscleName,
          image,
          choice: { kind, exerciseId: item.id, name: item.name, imageUrl: image, muscle: muscleName === '' ? null : muscleName },
        };
      }),
    };
  }

  if (kind === 'plan') {
    return {
      pending: plans.isPending,
      failed: plans.isError,
      rows: (plans.data ?? [])
        .filter((plan) => typeof plan.id === 'number' && plan.id > 0)
        .map((plan) => ({
          key: String(plan.id),
          title: plan.templateName ?? '',
          meta: [
            plan.category === null || plan.category === undefined || plan.category === ''
              ? ''
              : t(`planTemplates.category.${plan.category}`, { defaultValue: plan.category }),
            dateLabel(plan.startDate),
          ]
            .filter((part) => part !== '')
            .join(' · '),
          image: plan.coverImageUrl ?? null,
          choice: {
            kind: 'plan' as const,
            planAssignmentId: plan.id ?? 0,
            name: plan.templateName ?? '',
            category: plan.category ?? null,
            imageUrl: plan.coverImageUrl ?? null,
          },
        })),
    };
  }

  return {
    pending: sessions.isPending,
    failed: sessions.isError,
    rows: (sessions.data?.sessions ?? [])
      .filter((session) => typeof session.workoutExecutionId === 'number' && session.workoutExecutionId > 0)
      .map((session) => ({
        key: String(session.workoutExecutionId),
        title: session.label ?? '',
        meta: [
          dateLabel(session.date),
          session.plan ?? '',
          typeof session.setsTotal === 'number' && session.setsTotal > 0
            ? t('chat.rich.setsChip', { done: session.setsDone ?? 0, total: session.setsTotal })
            : '',
        ]
          .filter((part) => part !== '')
          .join(' · '),
        image: null,
        choice: {
          kind: 'summary' as const,
          workoutExecutionId: session.workoutExecutionId ?? 0,
          title: session.label ?? '',
          setsDone: session.setsDone ?? 0,
          setsTotal: session.setsTotal ?? 0,
        },
      })),
  };
}
